import { CaretRight, Plus } from '@/core/ui/icons';
import { useState } from 'react';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Page,
  PageHeader,
  Skeleton,
  type BadgeTone,
  FilterMenu,
} from '@/core/ui';
import { useEvents } from '../application/queries';
import { dateBadge, FORMATS, isPast, seatsLabel, STATUS_LABELS, type AcademyEvent } from '../domain/event';

const TONES: Record<string, BadgeTone> = { draft: 'warning', published: 'success', cancelled: 'danger' };
const TABS = [
  { id: 'proximos', label: 'Próximos' },
  { id: 'passados', label: 'Passados' },
  { id: 'todos', label: 'Todos' },
] as const;
type Tab = (typeof TABS)[number]['id'];

interface Props {
  canEdit: boolean;
  onOpen: (eventId: string) => void;
  onCreate: () => void;
}

/** Eventos da empresa — rascunhos, publicados e cancelados. */
export function EventsPage({ canEdit, onOpen, onCreate }: Props) {
  const events = useEvents();
  const [tab, setTab] = useState<Tab>('proximos');

  const all = events.data ?? [];
  const visible = all
    .filter((e) => (tab === 'todos' ? true : tab === 'passados' ? isPast(e) : !isPast(e)))
    .sort((a, b) =>
      tab === 'passados' ? b.starts_at.localeCompare(a.starts_at) : a.starts_at.localeCompare(b.starts_at),
    );

  return (
    <Page>
      <PageHeader
        title="Eventos"
        description="Congressos, lançamentos e encontros. Publicados aparecem no portal, com inscrição."
        actions={
          canEdit && (
            <Button onClick={onCreate}>
              <Plus aria-hidden className="size-4" />
              Novo evento
            </Button>
          )
        }
      />

      <FilterMenu
        label="Período"
        className="mb-4"
        value={tab}
        options={TABS.map((t) => ({ value: t.id, label: t.label }))}
        onChange={setTab}
      />

      {events.isPending ? (
        <Skeleton className="h-72 rounded-2xl" />
      ) : events.isError ? (
        <ErrorState error={events.error} onRetry={() => void events.refetch()} />
      ) : visible.length === 0 ? (
        <EmptyState
          title={all.length === 0 ? 'Nenhum evento ainda' : 'Nenhum evento neste período'}
          action={canEdit && all.length === 0 ? <Button onClick={onCreate}>Criar evento</Button> : undefined}
        />
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-2xl border border-line bg-surface">
          {visible.map((e) => (
            <Row key={e.id} event={e} onOpen={() => onOpen(e.id)} />
          ))}
        </ul>
      )}
    </Page>
  );
}

/** Uma linha por evento: a data em destaque, o nome e o essencial embaixo. */
function Row({ event: e, onOpen }: { event: AcademyEvent; onOpen: () => void }) {
  const badge = dateBadge(e.starts_at);
  const format = FORMATS.find((f) => f.id === e.format)?.label ?? e.format;
  const where = e.format === 'online' ? format : [format, e.city || e.venue].filter(Boolean).join(' · ');
  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className="group flex w-full items-center gap-5 px-6 py-4 text-left transition-colors hover:bg-surface-2"
      >
        <div className="flex size-14 shrink-0 flex-col items-center justify-center rounded-xl bg-surface-2">
          <span className="text-[11px] font-semibold text-brand-600">{badge.month}</span>
          <span className="text-xl leading-none font-bold text-fg">{badge.day}</span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="flex items-center gap-2">
            <span className="truncate text-base font-medium text-fg">{e.title}</span>
            {e.featured && <Badge tone="brand">Destaque</Badge>}
          </p>
          <p className="mt-1 truncate text-sm text-fg-subtle">
            {where} · {seatsLabel(e)}
          </p>
        </div>
        <Badge tone={TONES[e.status] ?? 'neutral'}>{STATUS_LABELS[e.status] ?? e.status}</Badge>
        <CaretRight
          aria-hidden
          className="size-4 shrink-0 text-gray-300 transition group-hover:translate-x-0.5 group-hover:text-gray-500"
        />
      </button>
    </li>
  );
}
